import {ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {PickerComponent} from '@ctrl/ngx-emoji-mart';
import {ReactionChoice} from '../message-reactions';

/**
 * THE REACTION BAR (ITP-E-194-05, ITP-E-207-04, ITP-E-240-09), after carbon's `.itp-chat-reactbar`: the six in a lozenge
 * and "+" for every emoji (B-10). "+" swaps the row for the composer's emoji picker in the same place, as carbon's
 * "More" swaps its row for the grid. The thread draws it in one overlay over the messages, anchored to the React button
 * of the message it reacts to (conversation.component.ts `openReactionPicker`); this component only draws it and says
 * what was picked. Tab or the arrow keys move along the keys.
 */
@Component({
  standalone: true,
  selector: 'messenger-reaction-picker',
  imports: [CommonModule, MatTooltipModule, PickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './messenger-reaction-picker.component.html',
  styleUrls: ['./messenger-reaction-picker.component.scss']
})
export class MessengerReactionPickerComponent {
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  /** The six (`REACTION_CHOICES`), in their order. */
  @Input() choices: readonly ReactionChoice[] = [];
  /** Whether "+" is offered: reacting with any emoji needs B-10. */
  @Input() anyEmoji = false;
  /** Every emoji in place of the six. */
  @Input() full = false;
  /** The glyph picked, from the six or from every emoji. */
  @Output() picked = new EventEmitter<string>();
  /** "+" was pressed. */
  @Output() more = new EventEmitter<void>();

  /** Left and Right step along the keys and wrap, Home and End go to the ends. */
  onKeydown(event: KeyboardEvent): void {
    const keys = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.reaction-picker-key'));
    const at = keys.indexOf(document.activeElement as HTMLElement);
    if (at < 0) {
      return;
    }
    const step: Record<string, number> = {ArrowRight: at + 1, ArrowLeft: at - 1 + keys.length, Home: 0, End: keys.length - 1};
    if (!(event.key in step)) {
      return;
    }
    event.preventDefault();
    keys[step[event.key] % keys.length].focus();
  }
}
